/**
 * Masonry: the items in sequence, each dropped into the shortest column so far, so cards of uneven
 * height leave no dead space. The column count follows the container's width and its two CSS custom
 * properties (--masonry-min-col, --masonry-max-cols); heights come from a ResizeObserver on every item.
 *
 * Placement is by position, not by item, and it freezes while `frozen` is true (a drag in progress)
 * and while anything inside has keyboard focus: cards then keep their slots, a live reorder only
 * changes which card sits in which slot, and nothing moves under a pointer or out from under a cursor.
 * Once focus leaves, the layout settles after a short pause so a click that stole focus still lands.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';

export interface MasonryItem { id: string; node: ReactNode }

/** After focus leaves the grid, how long before a pending relayout runs. */
const SETTLE_MS = 250;
const MIN_COL_FALLBACK_PX = 360;
const MAX_COLS_FALLBACK = 3;

function cssNumber(el: HTMLElement, name: string, fallback: number): number {
  const n = parseFloat(getComputedStyle(el).getPropertyValue(name));
  return Number.isFinite(n) ? n : fallback;
}

export function Masonry({ items, frozen = false, className }: { items: MasonryItem[]; frozen?: boolean; className?: string }) {
  const root = useRef<HTMLDivElement>(null);
  const heights = useRef(new Map<string, number>());
  const [cols, setCols] = useState(1);
  /** Column per sequence position. */
  const [slots, setSlots] = useState<number[]>([]);
  const [focused, setFocused] = useState(false);
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hold = frozen || focused;
  const holdRef = useRef(hold);
  holdRef.current = hold;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const colsRef = useRef(cols);
  colsRef.current = cols;

  const relayout = useCallback(() => {
    const list = itemsRef.current;
    const n = colsRef.current;
    const gap = root.current ? cssNumber(root.current, '--masonry-gap', 0) : 0;
    const known = list.every((it) => heights.current.has(it.id));
    const tops = new Array<number>(n).fill(0);
    const next = list.map((it, i) => {
      // Until every card has been measured, deal round-robin rather than piling them into one column.
      let c = i % n;
      if (known) {
        c = 0;
        for (let k = 1; k < n; k++) if (tops[k]! < tops[c]!) c = k;
      }
      tops[c] = tops[c]! + (heights.current.get(it.id) ?? 0) + gap;
      return c;
    });
    setSlots((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, []);

  // Column count from the container's width.
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      const minCol = cssNumber(el, '--masonry-min-col', MIN_COL_FALLBACK_PX);
      const maxCols = cssNumber(el, '--masonry-max-cols', MAX_COLS_FALLBACK);
      const n = Math.max(1, Math.min(maxCols, Math.floor(el.clientWidth / minCol)));
      setCols((c) => (c === n ? c : n));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // One observer for every card; a changed height asks for a relayout unless the grid is held.
  const observer = useRef<ResizeObserver | null>(null);
  if (observer.current === null && typeof ResizeObserver !== 'undefined') {
    observer.current = new ResizeObserver((entries) => {
      let changed = false;
      for (const e of entries) {
        const id = (e.target as HTMLElement).dataset.masonryId;
        if (!id) continue;
        const h = e.contentRect.height;
        if (heights.current.get(id) !== h) { heights.current.set(id, h); changed = true; }
      }
      if (changed && !holdRef.current) relayout();
    });
  }
  useEffect(() => () => observer.current?.disconnect(), []);
  const observe = useCallback((el: HTMLDivElement) => {
    observer.current?.observe(el);
    return () => observer.current?.unobserve(el);
  }, []);

  // A new sequence, a new column count or the end of a hold: lay out again.
  const ids = items.map((it) => it.id).join(' ');
  useLayoutEffect(() => {
    if (!hold) relayout();
  }, [ids, cols, hold, relayout]);

  const onFocus = () => {
    if (settle.current) { clearTimeout(settle.current); settle.current = null; }
    setFocused(true);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget && root.current?.contains(e.relatedTarget as Node)) return;
    if (settle.current) clearTimeout(settle.current);
    settle.current = setTimeout(() => { settle.current = null; setFocused(false); }, SETTLE_MS);
  };
  useEffect(() => () => { if (settle.current) clearTimeout(settle.current); }, []);

  const columns: MasonryItem[][] = Array.from({ length: cols }, () => []);
  items.forEach((it, i) => {
    const c = slots[i] ?? i % cols;
    columns[Math.min(c, cols - 1)]!.push(it);
  });

  return (
    <div ref={root} className={`masonry${className ? ` ${className}` : ''}`} onFocusCapture={onFocus} onBlurCapture={onBlur}>
      {columns.map((col, c) => (
        <div key={c} className="masonry-col">
          {col.map((it) => (
            <div key={it.id} ref={observe} data-masonry-id={it.id} className="masonry-item">{it.node}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
